/** The activity record's section registry (plan §3 "Cards", §9.5 "Spec enums for the VIEW
 *  halves", §10.3): every key the record shows → its card (About · Place · Visit · Contact &
 *  links · Nearby · Access · Identity & source · Technical), its position in the card, its
 *  block (the Editable fields, the muted read-only block of values no human edits — "From
 *  Google" or, for the ratings, "From rating sources" (`cards/layout`) — or the System block
 *  of derived, provenance and legacy values), its label and its widget.
 *
 *  The widget names the edit-mode input the EDIT half mounts and, through it, the read view
 *  the VIEW half shows where a value has a shape (the opening hours' seven rows, the
 *  rich-text box, the nearby list's numbered rows) — so pressing E moves nothing. Labels,
 *  kinds, cards, widgets and choices EQUAL the server's edit catalog
 *  (`GET /activities/edit/catalog/`, `apiv2/lib/console/activity_changeset/fields.py`; the
 *  contract test pins them to it): the page renders from the detail alone before the catalog
 *  arrives, then the EDIT half reads the catalog itself.
 *
 *  The same shape as the booking, order and user registries (`RegistryEntry`), so the records
 *  core lays all of them out alike; an activity has no lifecycle, so `stage` and `facet` are
 *  the card.
 *
 *  Contract (registry.contract.test.ts): the keys are exactly the spec enum
 *  `ActivityRecordFieldKey`, in its order; the editable ones exactly
 *  `ActivityRecordEditableFieldKey`; the cards exactly `ActivityRecordSection`. The map is
 *  typed by the generated enum as well, so a regenerated `types.ts` that adds or drops a key
 *  fails the typecheck before the test runs. */
import {
  ACTIVITY_TYPE_LABELS,
  DIRECTIONS_TYPE_LABELS,
} from '@/api/activities';
import {
  ACTIVITY_RECORD_SECTIONS,
  type ActivityRecordFieldKey,
  type ActivityRecordSection,
} from '@/api/activityRecord';
import { coreWidget } from '@/features/catalog/recordFields';
import type { RecordChoice, RecordField, RecordFieldKind } from '@/features/records/editor/types';
import type { FieldBlockKind, RegistryEntry } from '@/features/records/views/types';

import { PAYMENT_TYPE_LABELS, PRICE_TYPE_LABELS } from '../activitiesNamespaces';

/** The activity changes' `entity`. */
export const ACTIVITY_ENTITY = 'activity';

/** A field's kind in the backend catalog's terms: `editable` — an input in edit mode;
 *  `vendor` — from a source no human edits (the Google place, the rating sources), read-only;
 *  `derived` — system, provenance or legacy, read-only. */
export type ActivityFieldKind = Extract<RecordFieldKind, 'editable' | 'vendor' | 'derived'>;

export interface ActivityRegistryEntry extends RegistryEntry<ActivityRecordSection, ActivityRecordSection> {
  label: string;
  kind: ActivityFieldKind;
  /** The edit-mode input (and its read view); null for the read-only kinds. */
  widget: string | null;
  /** An enum widget's values, as stored. */
  choices?: readonly RecordChoice[];
}

const BLOCK_OF: Readonly<Record<ActivityFieldKind, FieldBlockKind>> = {
  editable: 'editable',
  vendor: 'vendor',
  derived: 'system',
};

function entry(
  section: ActivityRecordSection,
  order: number,
  kind: ActivityFieldKind,
  label: string,
  widget: string | null = null,
  choices?: readonly RecordChoice[],
): ActivityRegistryEntry {
  return {
    stage: section,
    facet: section,
    order,
    block: BLOCK_OF[kind],
    label,
    kind,
    widget,
    ...(choices ? { choices } : {}),
  };
}

const edit = (section: ActivityRecordSection, order: number, label: string, widget: string, choices?: readonly RecordChoice[]) =>
  entry(section, order, 'editable', label, widget, choices);
const fromSource = (section: ActivityRecordSection, order: number, label: string) => entry(section, order, 'vendor', label);
const system = (section: ActivityRecordSection, order: number, label: string) => entry(section, order, 'derived', label);

const fromLabels = (labels: Readonly<Record<number, string>>): RecordChoice[] =>
  Object.entries(labels)
    .map(([value, label]) => ({ value: Number(value), label }))
    .sort((a, b) => a.value - b.value);
const fromList = (labels: readonly string[]): RecordChoice[] => labels.map((label, value) => ({ value, label }));

/** `ACTIVITY_PRICE_LEVEL`: the app's dollar signs. */
const PRICE_LEVEL_CHOICES: RecordChoice[] = [1, 2, 3, 4].map((n) => ({ value: n, label: '$'.repeat(n) }));

export const ACTIVITY_RECORD_REGISTRY = {
  // ------------------------------------------------------------------- About
  name: edit('about', 10, 'Name', 'text'),
  display_name: edit('about', 20, 'Display name', 'text'),
  type: edit('about', 30, 'Type', 'enum', fromLabels(ACTIVITY_TYPE_LABELS)),
  category: edit('about', 40, 'Category', 'category'),
  preview_text: edit('about', 50, 'Preview text', 'text'),
  description: edit('about', 60, 'Description', 'rich_text'),

  // ------------------------------------------------------------------- Place
  address: edit('place', 10, 'Address', 'text'),
  coordinates: edit('place', 20, 'Coordinates', 'coordinates'),
  country: edit('place', 30, 'Country', 'country'),
  directions_type: edit('place', 40, 'Directions', 'enum', fromLabels(DIRECTIONS_TYPE_LABELS)),
  directions_url: edit('place', 50, 'Directions URL', 'url'),
  place: fromSource('place', 60, 'Google place'),
  timezone: system('place', 70, 'Time zone'),

  // ------------------------------------------------------------------- Visit
  duration: edit('visit', 10, 'Duration', 'duration'),
  opening_hours: edit('visit', 20, 'Opening hours', 'opening_hours'),
  price_type: edit('visit', 30, 'Price', 'enum', fromList(PRICE_TYPE_LABELS)),
  payment_type: edit('visit', 40, 'Payment', 'enum', fromList(PAYMENT_TYPE_LABELS)),
  cost_min: edit('visit', 50, 'Minimum price', 'number'),
  cost_max: edit('visit', 60, 'Maximum price', 'number'),
  currency: edit('visit', 70, 'Currency', 'currency'),
  cost_prefix: edit('visit', 80, 'Price prefix', 'text'),
  cost_unit: edit('visit', 90, 'Price unit', 'text'),
  price_level: edit('visit', 100, 'Price level', 'enum', PRICE_LEVEL_CHOICES),
  ratings: fromSource('visit', 110, 'Ratings'),

  // --------------------------------------------------------- Contact & links
  website: edit('contact', 10, 'Website', 'url'),
  phone: edit('contact', 20, 'Phone', 'phone'),
  email: edit('contact', 30, 'Email', 'email'),
  ext_link: edit('contact', 40, 'External link', 'url'),

  // ------------------------------------------------------------------ Nearby
  children: edit('nearby', 10, 'Nearby activities', 'activity_list'),
  nearby_of: system('nearby', 20, 'Nearby of'),

  // ------------------------------------------------------------------ Access
  atag: edit('access', 10, 'Access tags', 'atags'),

  // ------------------------------------------------------- Identity & source
  custom_id: edit('identity', 10, 'Custom ID', 'text'),
  internal_notes: edit('identity', 20, 'Internal notes', 'textarea'),
  source: system('identity', 30, 'Source'),
  ext_id: system('identity', 40, 'Import identity'),
  created_ts: system('identity', 50, 'Created'),
  updated_ts: system('identity', 60, 'Updated'),

  // --------------------------------------------------------------- Technical
  place_data: fromSource('technical', 10, 'Google place data'),
  version: system('technical', 20, 'App cache version'),
  notes: system('technical', 30, 'Notes (legacy)'),
  ontrip_description: system('technical', 40, 'On-trip description (legacy)'),
  description_abbrev: system('technical', 50, 'One-sentence description (legacy)'),
} as const satisfies Record<ActivityRecordFieldKey, ActivityRegistryEntry>;

export type ActivityRegistry = Readonly<Record<ActivityRecordFieldKey, ActivityRegistryEntry>>;

/** Card titles, in page order. */
export const ACTIVITY_SECTION_TITLES: Readonly<Record<ActivityRecordSection, string>> = {
  about: 'About',
  place: 'Place',
  visit: 'Visit',
  contact: 'Contact & links',
  nearby: 'Nearby',
  access: 'Access',
  identity: 'Identity & source',
  technical: 'Technical',
};

/** The keys grouped by card, ordered by card then position. Pure. */
export function activityPlacements(
  registry: ActivityRegistry = ACTIVITY_RECORD_REGISTRY,
): Map<ActivityRecordSection, ActivityRecordFieldKey[]> {
  const index = (s: ActivityRecordSection) => ACTIVITY_RECORD_SECTIONS.indexOf(s);
  const keys = (Object.keys(registry) as ActivityRecordFieldKey[]).sort((a, b) => {
    const x = registry[a];
    const y = registry[b];
    return index(x.stage) - index(y.stage) || x.order - y.order;
  });
  const out = new Map<ActivityRecordSection, ActivityRecordFieldKey[]>();
  for (const key of keys) {
    const section = registry[key].stage;
    const list = out.get(section);
    if (list) list.push(key);
    else out.set(section, [key]);
  }
  return out;
}

/** The registry as the records core's catalog (`RecordEditorConfig.fields`), in card order:
 *  what the FieldSlots read until the server's edit catalog arrives — the same labels, kinds
 *  and inputs (`coreWidget`) as the catalog's (the contract test), so nothing moves when it
 *  takes over. */
export function activityRecordFields(registry: ActivityRegistry = ACTIVITY_RECORD_REGISTRY): RecordField[] {
  return [...activityPlacements(registry).values()].flat().map((key) => {
    const e = registry[key];
    return {
      key,
      entity: ACTIVITY_ENTITY,
      label: e.label,
      kind: e.kind,
      widget: coreWidget(e.widget),
      choices: [...(e.choices ?? [])],
      guards: [],
      permissions: [],
      follows_lead: false,
      effects: [],
      automatic: [],
      restech_rule: null,
    };
  });
}
